import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, FacebookAuthProvider, GithubAuthProvider } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

try {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
} catch (e) {
  console.warn('App Check initialisation failed:', e);
}

// Exported by name because the thumbnail capability URLs are built by hand
// (signedUrls.js) rather than fetched from the SDK — reading it off the config
// keeps that construction out of the Storage instance's internals.
export const storageBucket = firebaseConfig.storageBucket;

export const auth      = getAuth(app);
export const storage   = getStorage(app);

// initializeFirestore rather than getFirestore, for two settings that cannot be
// applied after the instance exists.
//
// memoryLocalCache, and the IndexedDB-backed cache this used to carry is gone
// on purpose. It cost thirty seconds on an iPad.
//
// The trace, captured 2026-09-24 over Safari Web Inspector: the WebChannel
// session opened 0.55s after page load and answered its first byte in 29.7ms.
// Three operations went out at 0.73s and completed in 26 to 34ms each. Then the
// client sent Firestore nothing at all until 30.60s, on the same session id and
// the same gsessionid, so nothing had dropped or reconnected — and the requests
// it finally sent came back in 25ms. The page was not frozen either: an
// analytics beacon fired at 5.34s and our own watchdog at 20.46s, both inside
// the gap. Fast network, fast backend, a client that did not ask.
//
// The only thing sitting between getDoc() and the wire in that configuration
// was the persistence layer, and on WebKit it took about thirty seconds to
// become usable while the SDK declined to issue a single read. Signing in on
// the same iPad in a private tab, which starts with an empty throwaway
// IndexedDB, was instant. Firebase Auth uses IndexedDB too and signed the same
// session in at 0.49s, so this is Firestore's much heavier use of it rather
// than the browser's implementation being broken outright.
//
// What the change costs is small, and smaller than it looks: since Phase 2 the
// bulk tree load has been the snapshot endpoint, and tree discovery moved to
// the API in the same release as this comment. What the browser still reads
// from Firestore directly is the subscription document and two bounded delta
// listeners, none of which is on the path to first paint. Offline reads go
// away, and each tab now fetches for itself rather than sharing a cache, which
// is a few more reads per session and no lease to acquire.
//
// There is no per-browser branch here on purpose. A user-agent test would leave
// the same trap armed for whichever engine ships the next variation of it, and
// would mean the configuration we test on a laptop is not the one an iPad runs.
//
// experimentalAutoDetectLongPolling probes whether the WebChannel stream
// actually works and falls back to long polling when it does not. That is what
// clears the Firestore `Listen` 400s seen in Safari: pre-existing churn, always
// harmless because the SDK retries, but noise that makes a real error harder to
// see. It was not implicated in the stall above and stays.
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(),
  experimentalAutoDetectLongPolling: true,
});

export const functions = getFunctions(app);

// Analytics is not supported in some environments (e.g. Firefox with strict tracking protection,
// Node test runners). isSupported() guards against those cases.
//
// The promise is exported alongside the instance because `isSupported()` settles
// a tick or two after the module loads, and events fired during that window —
// a sign-up on a cold start, say — would find `analytics` still null and be lost
// with no sign that anything went missing. Callers in analytics.js await this
// rather than reading the variable, so the only events that vanish are the ones
// in browsers that genuinely refuse to measure.
export let analytics = null;
export const analyticsReady = isSupported()
  .then(yes => (analytics = yes ? getAnalytics(app) : null))
  .catch(() => null);

export const googleProvider   = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
export const githubProvider   = new GithubAuthProvider();

export default app;
